<script>
  import { onMount, beforeUpdate, onDestroy } from 'svelte';
  import { Chart, registerables } from 'chart.js';
  import annotationPlugin from 'chartjs-plugin-annotation';
  import { theme } from '$stores/themeStore';
  import { get } from 'svelte/store';
  import 'chartjs-adapter-date-fns';
  import { selectedStudy, selectedParticipant } from '$stores/selectionStore';
  import { studyProperties, isFeatureEnabled, fetchStudyProperties } from '$stores/configStore';
  import { glucoseunit } from '$stores/unitsStore';

  // Feature flag variables
  let isSMBGEnabled = true;
  let isDiaryEnabled = true;
  let isMessagingEnabled = true;

  // Reactive variables for feature flags
  $: {
    isSMBGEnabled = isFeatureEnabled('smbg');
    isDiaryEnabled = isFeatureEnabled('diary');
    isMessagingEnabled = isFeatureEnabled('messaging');
  }

  export let data = [];
  export let selectedDate;
  export let hoveredEvent = null;
  export let onEventClick = () => {};
  export let showEventMarkers = true;

  let canvas;
  let chart;
  let printMediaQuery;
  let hasPlayedInitialAnimation = false;

  // Default thresholds (will be overridden by study properties)
  let HIGH_THRESHOLD = 180;
  let LOW_THRESHOLD = 70;
  const HIGH_COLOR = 'rgba(255, 0, 20, 0.8)'; // Red for high
  const LOW_COLOR = 'rgba(255, 160, 0, 0.8)'; // Orange for low
  const NORMAL_COLOR = 'rgba(22, 163, 144, 0.8)'; // Default green

  $: isMmol = $glucoseunit === 'mmol/L';
  $: yAxisOptions = {
    title: {
      display: true,
      text: `Glucose (${$glucoseunit})`,
    },
    position: 'right',
    min: isMmol ? 0 : 0,
    max: isMmol ? 22.5 : 405,
    suggestedMin: isMmol ? 1 : 20,
    suggestedMax: isMmol ? 20.7 : 378,
  };

  const lightModeOptions = {
    scales: {
      x: {
        type: 'time',
        time: {
          unit: 'hour',
          displayFormats: {
            hour: 'HH:mm',
          },
          timezone: $selectedParticipant.timezoneCode,
        },
        title: {
          display: false,
          text: 'Date',
          color: 'rgba(0, 0, 0, 0.8)',
        },
        ticks: {
          color: 'rgba(0, 0, 0, 0.8)',
        },
        min: new Date(`${selectedDate}T00:00:00`),
        max: new Date(`${selectedDate}T23:59:59.999`),
      },
      y: {
        title: {
          display: true,
          text: 'Glucose (mg/dL)',
          color: 'rgba(0, 0, 0, 0.8)',
        },
        position: 'right',
        ticks: {
          color: 'rgba(0, 0, 0, 0.8)',
        },
        min: 0,
        max: 405,
        suggestedMin: 20,
        suggestedMax: 378,
      },
    },
    plugins: {
      legend: {
        display: true,
        labels: {
          color: 'rgba(0, 0, 0, 0.8)',
        },
      },
      canvas: {
        responsive: true,
        maintainAspectRatio: false,
        resizeDelay: 1000,
      },
      annotation: {
        annotations: {
          line1: {
            type: 'line',
            yMin: HIGH_THRESHOLD,
            yMax: HIGH_THRESHOLD,
            borderColor: HIGH_COLOR,
            borderWidth: 0.5,
            label: {
              display: true,
              content: `High: ${HIGH_THRESHOLD}`,
              position: 'start',
              backgroundColor: 'rgba(255, 255, 255, 0.0)',
              color: HIGH_COLOR,
              font: {
                size: 10
              },
              padding: 4,
              yAdjust: -10
            }
          },
          line2: {
            type: 'line',
            yMin: LOW_THRESHOLD,
            yMax: LOW_THRESHOLD,
            borderColor: LOW_COLOR,
            borderWidth: 0.5,
            label: {
              display: true,
              content: `Low: ${LOW_THRESHOLD}`,
              position: 'start',
              backgroundColor: 'rgba(255, 255, 255, 0.0)',
              color: LOW_COLOR,
              font: {
                size: 10
              },
              padding: 4,
              yAdjust: 9
            }
          },
          nowLine: {
            type: 'line',
            xMin: new Date(new Date().toLocaleString('en-US', { timeZone: $selectedParticipant.timezoneCode })),
            xMax: new Date(new Date().toLocaleString('en-US', { timeZone: $selectedParticipant.timezoneCode })),
            borderColor: 'rgba(128, 128, 128, 0.5)',
            borderWidth: 1,
            borderDash: [5, 5],
          },
        },
      },
    },
  };

  const darkModeOptions = {
    scales: {
      x: {
        type: 'time',
        time: {
          unit: 'hour',
          displayFormats: {
            hour: 'HH:mm',
          },
          timezone: $selectedParticipant.timezoneCode,
        },
        title: {
          display: false,
          text: 'Date',
          color: 'rgba(240, 240, 240, 0.8)',
        },
        ticks: {
          color: 'rgba(240, 240, 240, 0.8)',
        },
        min: new Date(`${selectedDate}T00:00:00`),
        max: new Date(`${selectedDate}T23:59:59.999`),
      },
      y: {
        title: {
          display: true,
          text: 'Glucose (mg/dL)',
          color: 'rgba(240, 240, 240, 0.8)',
        },
        position: 'right',
        ticks: {
          color: 'rgba(240, 240, 240, 0.8)',
        },
        min: 0,
        max: 405,
        suggestedMin: 20,
        suggestedMax: 378,
      },
    },
    plugins: {
      legend: {
        display: true,
        labels: {
          color: 'rgba(240, 240, 240, 0.8)',
        },
      },
      canvas: {
        responsive: true,
        maintainAspectRatio: false,
        resizeDelay: 1000,
      },
      annotation: {
        annotations: {
          line1: {
            type: 'line',
            yMin: HIGH_THRESHOLD,
            yMax: HIGH_THRESHOLD,
            borderColor: HIGH_COLOR,
            borderWidth: 0.5,
            label: {
              display: true,
              content: `High: ${HIGH_THRESHOLD}`,
              position: 'start',
              backgroundColor: 'rgba(50, 50, 50, 0.0)',
              color: HIGH_COLOR,
              font: {
                size: 10
              },
              padding: 4,
              yAdjust: -10
            }
          },
          line2: {
            type: 'line',
            yMin: LOW_THRESHOLD,
            yMax: LOW_THRESHOLD,
            borderColor: LOW_COLOR,
            borderWidth: 0.5,
            label: {
              display: true,
              content: `Low: ${LOW_THRESHOLD}`,
              position: 'start',
              backgroundColor: 'rgba(50, 50, 50, 0.0)',
              color: LOW_COLOR,
              font: {
                size: 10
              },
              padding: 4,
              yAdjust: 9
            }
          },
          nowLine: {
            type: 'line',
            xMin: new Date(new Date().toLocaleString('en-US', { timeZone: $selectedParticipant.timezoneCode })),
            xMax: new Date(new Date().toLocaleString('en-US', { timeZone: $selectedParticipant.timezoneCode })),
            borderColor: 'rgba(128, 128, 128, 0.5)',
            borderWidth: 1,
            borderDash: [5, 5],
          },
        },
      },
    },
  };

  let updateInterval;

  function toParticipantTime(timestamp, timezone = $selectedParticipant.timezoneCode) {
    return new Date(
      new Date(timestamp).toLocaleString('en-US', {
        timeZone: timezone,
      })
    );
  }

  function formatXAxisTickLabel(value) {
    const date = new Date(value);
    const hours = String(date.getHours()).padStart(2, '0');
    return hours === '00' ? '00' : `${hours}:00`;
  }

  function getDateKeyInTimezone(timestamp, timezone = $selectedParticipant?.timezoneCode) {
    if (!timestamp || !timezone) {
      return '';
    }

    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(new Date(timestamp));

    const year = parts.find((part) => part.type === 'year')?.value;
    const month = parts.find((part) => part.type === 'month')?.value;
    const day = parts.find((part) => part.type === 'day')?.value;

    return year && month && day ? `${year}-${month}-${day}` : '';
  }

  function getCurrentParticipantTime() {
    return new Date(
      new Date().toLocaleString('en-US', {
        timeZone: $selectedParticipant?.timezoneCode || 'UTC',
      })
    );
  }

  function buildNowLineAnnotation(now = getCurrentParticipantTime()) {
    const isToday = selectedDate && selectedDate === getDateKeyInTimezone(now);

    return {
      type: 'line',
      display: isToday,
      xMin: now,
      xMax: now,
      borderColor: 'rgba(128, 128, 128, 0.5)',
      borderWidth: 1,
      borderDash: [5, 5],
    };
  }

  function updateNowLine() {
    if (chart?.options?.plugins?.annotation?.annotations) {
      chart.options.plugins.annotation.annotations.nowLine = buildNowLineAnnotation();
      chart.update('none');
    }
  }

  onMount(async () => {
    // Fetch study properties to get glucose thresholds
    if ($selectedStudy?._id) {
      await fetchStudyProperties($selectedStudy._id);
    }

    // Validate data
    if (!Array.isArray(data) || data.length === 0) {
      console.error('ScatterChart received invalid data: ', data.length);
      return;
    }

    //console.info('data', data);

    Chart.register(...registerables, annotationPlugin);
    createChart();

    // Start updating the 'now' line every 5 minutes
    updateInterval = setInterval(updateNowLine, 5 * 60 * 1000);

    window.addEventListener('beforeprint', forceLightModeForPrint);
    window.addEventListener('afterprint', restoreThemeAfterPrint);

    printMediaQuery = window.matchMedia('print');
    printMediaQuery.addEventListener('change', handlePrintMediaChange);
  });

  beforeUpdate(() => {
    if (chart) {
      chart.destroy();
      createChart();
    }
  });

  $: if (chart) {
    chart.destroy();
    createChart();
  }

  // Watch for unit changes and update the chart
  $: {
    if (chart) {
      const isMmol = $glucoseunit === 'mmol/L';
      
      // Update y-axis configuration
      chart.options.scales.y = {
        ...chart.options.scales.y,
        min: isMmol ? 0 : 0,
        max: isMmol ? 22.5 : 405,
        suggestedMin: isMmol ? 1 : 20,
        suggestedMax: isMmol ? 20.7 : 378,
        title: {
          display: true,
          text: `Glucose (${$glucoseunit})`,
          color: get(theme) === 'dark' ? 'rgba(240, 240, 240, 0.8)' : 'rgba(0, 0, 0, 0.8)',
        }
      };

      // Update threshold lines using study properties if available
      const high = isMmol ? 
        ($studyProperties?.glucoseParameters?.thresholds?.high || 180) / 18 : 
        ($studyProperties?.glucoseParameters?.thresholds?.high || 180);
      const low = isMmol ? 
        ($studyProperties?.glucoseParameters?.thresholds?.low || 70) / 18 : 
        ($studyProperties?.glucoseParameters?.thresholds?.low || 70);
      
      chart.options.plugins.annotation.annotations.line1.yMin = high;
      chart.options.plugins.annotation.annotations.line1.yMax = high;
      chart.options.plugins.annotation.annotations.line1.label.content = `High: ${high.toFixed(1)}`;
      chart.options.plugins.annotation.annotations.line2.yMin = low;
      chart.options.plugins.annotation.annotations.line2.yMax = low;
      chart.options.plugins.annotation.annotations.line2.label.content = `Low: ${low.toFixed(1)}`;

      chart.update('none');
    }
  }

  function createChart() {
    const currentTheme = get(theme);
    const options = structuredClone(currentTheme === 'dark' ? darkModeOptions : lightModeOptions);
    const isMmol = $glucoseunit === 'mmol/L';
    const high = isMmol ? 
      ($studyProperties?.glucoseParameters?.thresholds?.high || 180) / 18 : 
      ($studyProperties?.glucoseParameters?.thresholds?.high || 180);
    const low = isMmol ? 
      ($studyProperties?.glucoseParameters?.thresholds?.low || 70) / 18 : 
      ($studyProperties?.glucoseParameters?.thresholds?.low || 70);

    // Set initial y-axis configuration
    options.scales.y = {
      title: {
        display: true,
        text: `Glucose (${$glucoseunit})`,
        color: currentTheme === 'dark' ? 'rgba(240, 240, 240, 0.8)' : 'rgba(0, 0, 0, 0.8)',
      },
      position: 'right',
      ticks: {
        color: currentTheme === 'dark' ? 'rgba(240, 240, 240, 0.8)' : 'rgba(0, 0, 0, 0.8)',
      },
      min: isMmol ? 0 : 0,
      max: isMmol ? 22.5 : 405,
      suggestedMin: isMmol ? 1 : 20,
      suggestedMax: isMmol ? 20.7 : 378,
    };

    // Set threshold lines
    options.plugins.annotation.annotations.line1 = {
      type: 'line',
      yMin: high,
      yMax: high,
      borderColor: HIGH_COLOR,
      borderWidth: 0.5,
      label: {
        display: true,
        content: `High: ${high.toFixed(1)}`,
        position: 'start',
        backgroundColor: currentTheme === 'dark' ? 'rgba(50, 50, 50, 0.0)' : 'rgba(255, 255, 255, 0.0)',
        color: HIGH_COLOR,
        font: { size: 10 },
        padding: 4,
        yAdjust: -10
      }
    };

    options.plugins.annotation.annotations.line2 = {
      type: 'line',
      yMin: low,
      yMax: low,
      borderColor: LOW_COLOR,
      borderWidth: 0.5,
      label: {
        display: true,
        content: `Low: ${low.toFixed(1)}`,
        position: 'start',
        backgroundColor: currentTheme === 'dark' ? 'rgba(50, 50, 50, 0.0)' : 'rgba(255, 255, 255, 0.0)',
        color: LOW_COLOR,
        font: { size: 10 },
        padding: 4,
        yAdjust: 9
      }
    };

    options.plugins.annotation.annotations.nowLine = buildNowLineAnnotation();

    if (hoveredEvent?.timestamp) {
      const hoveredTime = toParticipantTime(
        hoveredEvent.timestamp,
        hoveredEvent.timezone || $selectedParticipant.timezoneCode
      );
      const windowMinutes = 15;
      const xMin = new Date(hoveredTime.getTime() - windowMinutes * 60 * 1000);
      const xMax = new Date(hoveredTime.getTime() + windowMinutes * 60 * 1000);

      options.plugins.annotation.annotations.eventHoverBand = {
        type: 'box',
        xMin,
        xMax,
        yMin: options.scales.y.min,
        yMax: options.scales.y.max,
        backgroundColor:
          currentTheme === 'dark' ? 'rgba(125, 211, 252, 0.10)' : 'rgba(14, 165, 233, 0.10)',
        borderWidth: 0,
      };

      options.plugins.annotation.annotations.eventHoverLine = {
        type: 'line',
        xMin: hoveredTime,
        xMax: hoveredTime,
        borderColor:
          currentTheme === 'dark' ? 'rgba(125, 211, 252, 0.65)' : 'rgba(3, 105, 161, 0.55)',
        borderWidth: 1,
        borderDash: [4, 4],
      };
    } else {
      delete options.plugins.annotation.annotations.eventHoverBand;
      delete options.plugins.annotation.annotations.eventHoverLine;
    }

    const ctx = canvas.getContext('2d');
    chart = new Chart(ctx, {
      type: 'scatter',
      data: {
        datasets: [
          {
            label: 'CGM',
            data: data.filter((point) => point.type === 'CGM').map((point) => ({
              ...point,
              y: isMmol && point.y != null ? point.y / 18 : point.y,
              sensorId: point.sensorId
            })),
            backgroundColor: NORMAL_COLOR, // for legend
            pointBackgroundColor: (context) => {
              if (!context?.raw?.y) return NORMAL_COLOR;
              const value = context.raw.y;
              if (value >= high) return HIGH_COLOR;
              if (value <= low) return LOW_COLOR;
              return NORMAL_COLOR;
            },
            borderColor: NORMAL_COLOR,
            pointRadius: 2,
            pointHoverRadius: 10,
            hitRadius: 10,
            borderWidth: 0,
            hoverBorderWidth: 0,
          },
          ...(isSMBGEnabled ? [{
            label: 'SMBG',
            data: data.filter((point) => point.type !== 'CGM' && point.type !== 'form' && point.type !== 'message').map((point) => ({
              ...point,
              y: isMmol && point.y != null ? point.y / 18 : point.y
            })),
            backgroundColor: 'rgba(255, 99, 132, 0.9)',
            pointRadius: 3,
            pointHoverRadius: 10,
            hitRadius: 10,
            borderWidth: 0,
            hoverBorderWidth: 0,
          }] : []),
          ...(showEventMarkers && isDiaryEnabled ? [{
            label: 'Diary Entries',
            data: data.filter((point) => point.type === 'form').map((point) => ({
              ...point,
              y: options.scales.y.min + (isMmol ? 1 : 12), // Place forms at bottom
              event: point.event,
            })),
            backgroundColor: 'rgba(10, 150, 200, 0.9)',
            pointStyle: 'rectRounded',
            pointRadius: 3,
            pointHoverRadius: 10,
            hitRadius: 10,
            borderWidth: 0,
            hoverBorderWidth: 0,
          }] : []),
          ...(showEventMarkers && isMessagingEnabled ? [{
            label: 'Alerts/Messages',
            data: data.filter((point) => point.type === 'message').map((point) => ({
              ...point,
              y: options.scales.y.min + (isMmol ? 0.2 : 3), // Place messages at very bottom
              event: point.event,
            })),
            backgroundColor: 'rgba(255, 206, 86, 0.9)',
            pointStyle: 'triangle',
            pointRadius: 4,
            pointHoverRadius: 10,
            hitRadius: 10,
            borderWidth: 0,
            hoverBorderWidth: 0,
          }] : []),
        ],
      },
      options: {
        ...options,
        animation: hasPlayedInitialAnimation
          ? false
          : {
              duration: 700,
              easing: 'easeOutQuart',
            },
        elements: {
          point: {
            hitRadius: 10,
            hoverRadius: 10,
            radius: 3,
            borderWidth: 0,
            hoverBorderWidth: 0,
          }
        },
        maintainAspectRatio: false,
        plugins: {
          ...options.plugins,
          tooltip: {
            callbacks: {
              label: function (context) {
                const point = context.raw;
                if (!point) return '';
                if (point.event) {
                  const time = new Date(point.event.timestamp || point.x).toLocaleString('en-US', {
                    timeZone: point.event.timezone || $selectedParticipant.timezoneCode,
                    hour: '2-digit',
                    minute: '2-digit',
                  });
                  return `${point.event.typeLabel}: ${point.event.title} at ${time}`;
                }
                const glucose = point.y != null ? point.y.toFixed(isMmol ? 1 : 0) : 'N/A';
                const time = new Date(point.x).toLocaleTimeString('en-US', {
                  hour: '2-digit',
                  minute: '2-digit',
                });
                const backfilledLabel = point.backfilled ? ' (b)' : '';
                return `${context.dataset.label}: ${glucose} ${$glucoseunit} at ${time}${backfilledLabel}`;
              },
            },
          },
        },
        scales: {
          ...options.scales,
          x: {
            ...options.scales.x,
            type: 'time',
            time: {
              unit: 'hour',
              displayFormats: {
                hour: 'HH:mm',
              },
              timezone: $selectedParticipant.timezoneCode,
            },
            min: new Date(`${selectedDate}T00:00:00`),
            max: new Date(`${selectedDate}T23:59:59.999`),
            ticks: {
              ...options.scales.x.ticks,
              source: 'auto',
              autoSkip: true,
              callback: (value) => formatXAxisTickLabel(value),
            },
          },
        },
        onClick: (event, elements) => {
          //console.log('Chart clicked!');
          //console.log('Elements:', elements);
          
          if (elements.length > 0) {
            const datasetIndex = elements[0].datasetIndex;
            const index = elements[0].index;
            
            //console.log('Dataset Index:', datasetIndex);
            //console.log('Dataset Label:', chart.data.datasets[datasetIndex].label);
            
            const clickedPoint = chart.data.datasets[datasetIndex].data[index];

            if (clickedPoint?.event) {
              onEventClick(clickedPoint.event);
            }
          }
        },
      },
    });

    hasPlayedInitialAnimation = true;
  }

  theme.subscribe(() => {
    if (chart) {
      chart.destroy();
      createChart();
    }
  });

  // Clean up the interval when the component is destroyed
  onDestroy(() => {
    if (updateInterval) {
      clearInterval(updateInterval);
    }
    window.removeEventListener('beforeprint', forceLightModeForPrint);
    window.removeEventListener('afterprint', restoreThemeAfterPrint);
    if (printMediaQuery) {
      printMediaQuery.removeEventListener('change', handlePrintMediaChange);
    }
  });

  let originalTheme = null;

  function forceLightModeForPrint() {
    if (chart) {
      originalTheme = get(theme);
      chart.destroy();
      // Use lightModeOptions regardless of current theme
      const options = structuredClone(lightModeOptions);
      // (re-use your createChart logic, but force light mode)
      const isMmol = $glucoseunit === 'mmol/L';
      const high = isMmol ? 10 : 180;
      const low = isMmol ? 3.9 : 70;
      options.scales.y = {
        title: {
          display: true,
          text: `Glucose (${$glucoseunit})`,
          color: 'rgba(0, 0, 0, 0.8)',
        },
        position: 'right',
        ticks: {
          color: 'rgba(0, 0, 0, 0.8)',
        },
        min: isMmol ? 0 : 0,
        max: isMmol ? 22.5 : 405,
        suggestedMin: isMmol ? 1 : 20,
        suggestedMax: isMmol ? 20.7 : 378,
      };
      options.plugins.annotation.annotations.line1.yMin = high;
      options.plugins.annotation.annotations.line1.yMax = high;
      options.plugins.annotation.annotations.line1.label.content = `High: ${high.toFixed(1)}`;
      options.plugins.annotation.annotations.line2.yMin = low;
      options.plugins.annotation.annotations.line2.yMax = low;
      options.plugins.annotation.annotations.line2.label.content = `Low: ${low.toFixed(1)}`;
      options.plugins.annotation.annotations.nowLine = buildNowLineAnnotation();
      const ctx = canvas.getContext('2d');
      chart = new Chart(ctx, {
        type: 'scatter',
        data: chart.data,
        options: {
          ...options,
        }
      });
    }
  }

  function restoreThemeAfterPrint() {
    if (chart && originalTheme) {
      chart.destroy();
      createChart();
      originalTheme = null;
    }
  }

  function handlePrintMediaChange(e) {
    if (e.matches) {
      // Entering print preview
      forceLightModeForPrint();
    } else {
      // Exiting print preview
      restoreThemeAfterPrint();
    }
  }
</script>

<canvas bind:this={canvas} class="print:w-[700px] print:max-w-full print:h-auto"></canvas>
